import { FileArrowUpIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { FileMimeTypes, Spinner } from '@/modules/common';

import { useEventParticipants, useEventParticipantTypes } from '../api';
import { CreateParticipantTypeDialog } from './create-participant-type-dialog';
import { ParticipantsEventActions } from './participants-event-actions';
import { ParticipantsTable } from './participants-table';

interface ParticipantTableProps {
  eventId: string;
  editMode?: boolean;
  eventName?: string;
}

export function Participants({
  eventId,
  editMode = false,
  eventName = '',
}: ParticipantTableProps) {
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [
    isCreateParticipantTypeDialogOpen,
    setIsCreateParticipantTypeDialogOpen,
  ] = useState(false);

  const {
    isFetching,
    participants,
    uploadEventParticipants,
    isGeneratingReport,
    getGenerateReportUrl,
  } = useEventParticipants(eventId, eventName);
  const { participantTypes, isFetching: isFetchingParticipantTypes } =
    useEventParticipantTypes(eventId, true);

  const pickFile = () => {
    fileInputRef.current.click();
  };

  const uploadParticipant = async (file: File) => {
    setIsUploading(true);
    try {
      await uploadEventParticipants(file);
      await queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const pickFileAndUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.length) return;

    const file = event.target.files[0];

    if (editMode) {
      await uploadParticipant(file);
    }
  };

  const formattedParticipants = useMemo(
    () =>
      participants.map((participant) => {
        return {
          ...participant,
          fullName: `${participant.firstName} ${participant.lastName}`,
        };
      }),
    [participants]
  );

  if (isFetching || isUploading || isFetchingParticipantTypes) {
    return <Spinner />;
  }

  return (
    <div>
      {participantTypes.length > 0 ? (
        <ParticipantsTable
          eventId={eventId}
          editMode={editMode}
          participantTypes={participantTypes}
          participants={formattedParticipants}
          onParticipantTypesClick={() =>
            setIsCreateParticipantTypeDialogOpen(true)
          }
        />
      ) : (
        <div className="flex items-center justify-center flex-col gap-5 py-10">
          <p className="text-zinc-50 font-semibold text-lg">
            Create Participant Types before uploading the Participant CSV file.
          </p>
          <p className="text-zinc-400">
            {editMode
              ? 'Use the "Create Participant Types" button below to get started.'
              : 'You can create "Participant Types" only in published event.'}
          </p>

          {editMode && (
            <Button
              onClick={() => setIsCreateParticipantTypeDialogOpen(true)}
              data-testid="create-participant-types-button"
            >
              Create Participant Types
            </Button>
          )}
        </div>
      )}

      {editMode && (
        <div className="mt-4 flex gap-2">
          <Button
            disabled={isFetching || !participantTypes.length}
            variant="transparent"
            onClick={pickFile}
            data-testid="upload-file-button"
          >
            <FileArrowUpIcon />
            Upload from CSV
            <input
              id="csv-file"
              type="file"
              ref={fileInputRef}
              className="hidden"
              data-testid="upload-file-input"
              onChange={pickFileAndUpload}
              accept={FileMimeTypes.Csv}
            />
          </Button>

          {formattedParticipants.length > 0 && (
            <ParticipantsEventActions
              eventId={eventId}
              onGenerateReport={getGenerateReportUrl}
              isGeneratingReport={isGeneratingReport}
            />
          )}
        </div>
      )}

      {isCreateParticipantTypeDialogOpen && (
        <CreateParticipantTypeDialog
          isOpen
          eventId={eventId}
          participants={formattedParticipants}
          participantTypes={participantTypes}
          onOpenChange={setIsCreateParticipantTypeDialogOpen}
        />
      )}
    </div>
  );
}
